<script setup lang="ts">
import { computed, ref } from 'vue'

import { contentfulUrl } from '@internal/format-contentful'
import Button from '@satch-ui/components/button/Button.vue'
import TeaserCardItemContent from '@storefront-satch/components/molecules/teaser-card/TeaserCardItemContent.vue'
import TeaserCardItemMedia from '@storefront-satch/components/molecules/teaser-card/TeaserCardItemMedia.vue'
import type {
  TeaserCardItemAdditionalProps,
  TeaserCardItemProps,
} from '@storefront-satch/components/molecules/teaser-card/types'
import { clsx } from 'clsx'

import { PlayIcon } from '@fond-of/satch-icons'

const props = withDefaults(
  defineProps<TeaserCardItemProps & TeaserCardItemAdditionalProps>(),
  {
    mediaAspectRatio: '2/1',
  }
)

const isVideoPlaying = ref<boolean>(false)

const hasVideo = computed(
  () =>
    !!(
      props.media?.videoMobile?.url ||
      props.media?.videoTablet?.url ||
      props.media?.videoDesktop?.url
    )
)
const videoHasControls = computed(() => props.media?.videoControls)
const videoIsAutoplay = computed(() => props.media?.videoAutoplay)
const hasLinkAndLinkText = computed(() => !!(props.link && props.linkText))

const width = computed(() => props.width ?? 'single')
const type = computed(() => props.type ?? 'half')

const isFullVideo = computed(() => {
  return hasVideo.value && type.value === 'full'
})

const showVideoWithLinkButton = computed(() => {
  if (
    isFullVideo.value &&
    videoIsAutoplay.value &&
    !videoHasControls.value &&
    hasLinkAndLinkText.value
  ) {
    return true
  }

  return type.value === 'half' && hasLinkAndLinkText.value
})

const showVideoWithPlayButton = computed(
  () => isFullVideo.value && !videoIsAutoplay.value && !videoHasControls.value
)

const isLinkElement = computed(() => {
  return props.link || showVideoWithLinkButton.value
})
</script>

<template>
  <div
    :id="props.sys.id"
    :class="[
      'relative w-full shrink-0 translate-y-0 transition-transform duration-300 ease-out hover:md:translate-y-2.5',
      {
        // slider
        'mb-6 inline-block w-full py-0 sm:mb-5 sm:block sm:w-auto sm:p-0':
          props.groupType === 'slider',
      },
    ]"
    :data-cfid="props.sys.id"
    data-label="Teaser Card Item"
    :data-title="props.internalTitle"
  >
    <component
      :is="isLinkElement ? 'a' : 'div'"
      class="relative flex flex-wrap content-start p-0 hover:no-underline"
      :href="contentfulUrl(props.link)"
      :data-gtm-attr="isLinkElement ? 'link_teaser-card-item-small' : undefined"
      :data-gtm-label="isLinkElement ? props.internalTitle : undefined"
      :aria-label="
        isLinkElement
          ? props.linkAriaLabel?.trim() || props.linkText?.trim() || undefined
          : undefined
      "
    >
      <TeaserCardItemMedia
        v-if="props.media"
        :class="{
          'mb-[25px]':
            !props.content?.headline &&
            !props.content?.subline &&
            hasLinkAndLinkText,
          'mb-4': props.content?.headline || props.content?.subline,
        }"
        :media="props.media"
        :type="props.type"
        :size="size"
        :width="width"
        :group-type="props.groupType"
        :media-aspect-ratio="props.mediaAspectRatio"
        :media-fit="props.mediaFit"
        @update:video-playing="
          (state) => {
            isVideoPlaying = state
          }
        "
      />

      <TeaserCardItemContent
        :hide-content="isVideoPlaying"
        :is-clickable="hasLinkAndLinkText && !isVideoPlaying"
        :headline="props.content?.headline"
        headline-size="medium"
        :headline-level="props.content?.headlineLevel || null"
        :subline="props.content?.subline"
        :text="props.content?.text"
        :content-style="
          props.contentStyle || (type === 'full' ? 'white' : 'black')
        "
        :link-style="props.linkStyle"
        :text-size="type === 'full' ? 'medium' : 'small'"
        :is-text-compact="props.groupType === 'slider'"
        :content-alignment="props.content?.alignment || 'left'"
        :class="[
          clsx('md:order-0 order-10 m-0 flex w-full flex-col flex-wrap', {
            'lg:px-2.5': type === 'half',
            'absolute bottom-0 left-0 top-0 mb-0 flex flex-col justify-end pb-[30px] pl-[20px] pr-[20px] pt-[10px] text-white md:px-[20px] lg:py-[40px] lg:pl-[50px] lg:pr-[20px]':
              type === 'full',
            'md:w-[65%]': width === 'double' && props.size !== 'small',
          }),
        ]"
      >
        <template #contentFooter>
          <Button
            v-if="
              (hasLinkAndLinkText && !hasVideo) ||
              showVideoWithLinkButton ||
              showVideoWithPlayButton
            "
            as="span"
            class="font-sans-medium mt-[10px] w-fit md:mt-5"
            :class="{ 'mx-auto': props.content?.alignment === 'center' }"
            :variant="props.linkStyle || 'outlineWhite'"
            :hover="false"
          >
            {{ props.linkText }}

            <PlayIcon
              v-if="isFullVideo && !videoIsAutoplay"
              class="size-4"
            />
          </Button>
        </template>
      </TeaserCardItemContent>
    </component>
  </div>
</template>
